import { navigate } from 'svelte5-router'
import { pageError } from '../stores'
import { getJwt } from './jwt'

/** pathname + query of the current page — the value legacy passes as ?ret= / ?redirect= */
function getCurrentReturnUrl(): string {
	return window.location.pathname + window.location.search
}

/**
 * auth failure = go log in, not an error page. Mirrors the legacy contract of
 * carrying the original URL (NoAccess401's own button: /login?redirect=<ret>),
 * minus the /401 interstitial with its misleading permissions text.
 * Hard navigation on purpose: /login is the legacy pasta login flow.
 */
export function redirectToLoginWithReturn(): void {
	if (typeof window === 'undefined') return
	// already on the login flow — a second redirect would loop via its own API calls
	if (window.location.pathname.startsWith('/login')) return

	window.location.assign(`/login?redirect=${encodeURIComponent(getCurrentReturnUrl())}`)
}

/**
 * fatal load error on a pub page: route to the designed /404 page (App.svelte's
 * own route with $t('UI.404.desc')) — the client twin of the backend's
 * ERROR_404 -> "/404?ret=" redirect in SvelteController.respondWithSvelteApp.
 * svelte5-router's navigate(), not history.pushState — the router only picks up
 * its own navigations and popstate.
 */
export function redirectToNotFoundRoute(): void {
	if (typeof window === 'undefined') return

	navigate(`/404?ret=${encodeURIComponent(getCurrentReturnUrl())}`)
}

/**
 * Kit-shell workaround for lost document 307→/401: doHandle already painted
 * PubPage404 for a handled API 404, but when the visitor is logged out that
 * 404 is often auth-before-404 from the backend (OfferUser / latestRequest
 * missing before OfferUserNotLoggedIn). Redirect to login with return URL.
 *
 * FIXME(mineralminds): OptimizerApiFacade.processGetRequestCollectingWebRequest
 * must mirror FrontendController.checkUserIsCurrentUser — emit 401/403 for
 * SiriusUserWrapper session failures *before* 404-before-auth — so this
 * heuristic can die. Collateral until then: logged-out ExternalUser true-404
 * (dead magic link) also redirects to login.
 *
 * @returns true when a login redirect was triggered
 */
export async function redirectToLoginOnLoggedOutHandled404(error: {
	code?: number
	handled?: boolean
}): Promise<boolean> {
	if (!(error?.handled && error.code === 404)) return false

	let loggedIn: boolean
	try {
		loggedIn = await getJwt()
	} catch {
		return false
	}
	if (loggedIn) return false

	pageError.set(undefined as never)
	redirectToLoginWithReturn()
	return true
}
